// Canvas.ts
import '../css/Canvas.css';

import { Pane } from 'tweakpane';
import * as TweakpaneRotation from '@0b5vr/tweakpane-plugin-rotation';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader, type GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { MotionBloomPass, type MotionBloomParams } from './MotionBloomPass';
import { Vector2 } from 'three';
import type { EulerOrder } from 'three';
import { appState } from './AppState';
import type { AppState, SettingsIndex } from '../types';
import { CATEGORIES, PROGRAMMES } from './DataManager.details';

export default class Canvas {
	currentModelIndex = 0;

	originalMaterials: THREE.Material[] = [];
	depthMeshes: THREE.Mesh[] = [];
	multipleModels: THREE.Group[] = [];
	activeLoadId = 0;
	modelCache = new Map<string, THREE.Group>();

	bloomPass!: MotionBloomPass;
	camera!: THREE.PerspectiveCamera;
	dirLight!: THREE.DirectionalLight;
	controls!: OrbitControls;
	scene!: THREE.Scene;
	renderer!: THREE.WebGLRenderer;
	composer!: EffectComposer;
	clock!: THREE.Clock;

	originalModel: THREE.Group | null = null;
	axisHelper: THREE.AxesHelper | null = null;

	settings: SettingsIndex = {};

	parent: HTMLElement | null = null;

	_pane!: Pane;
	rendererParams: any;
	bloomParams: any;
	lightParams: any;

	_savedSettings: any = {};
	lerpParams?: any;
	lerpStart?: any;
	lerpProgress?: number;
	baseBloomRadius?: number;

	allowPane: boolean;

	constructor() {
		this.parent = document.getElementById('profiles');
		this.allowPane = false;
	}

	async init(): Promise<void> {
		await this.loadData(); // ensure settings present before building scene
		this.initThree();
		this.loadModel(String(this.currentModelIndex));
		this.startLoop();
		this.bindEvents();
		this.initPane();

		const paneElem = (this._pane as any).element as HTMLElement;
		if (paneElem) paneElem.style.display = 'none';

		appState.subscribe(this.onStateChange.bind(this));
	}

	onStateChange(state: AppState): void {
		if (!state.data) return;
		if (state.categoryIndex == null || state.programmeIndex == null) {
			if (this.currentModelIndex === 0) return;
			this.loadModel('0');
			return;
		}
		const currentTheme = CATEGORIES[state.categoryIndex];
		const currentProgramme = PROGRAMMES[state.programmeIndex];
		if (!currentTheme || !currentProgramme) return;
		let index = null;
		for (const key of Object.keys(state.data)) {
			const element = state.data[parseInt(key) || -1];
			if (!element) continue;
			const programme = element.programme?.toString().replace(' MA', '');
			if (element.theme === currentTheme && programme === currentProgramme) {
				index = key;
				break;
			}
		}
		if (index != null) {
			if (index == String(this.currentModelIndex)) return;
			const indexValue = parseInt(index);
			const direction = indexValue > this.currentModelIndex ? 'right' : 'left';
			this.loadModel(index, direction);
		}
	}

	async loadData(): Promise<void> {
		try {
			const resp = await fetch('/settings.json');
			if (resp.ok) {
				this.settings = (await resp.json()) as SettingsIndex;
			} else {
				this.settings = {};
			}
		} catch {
			this.settings = {};
		}

		// Now try to load settings.json, fallback to localStorage if missing
		let loadedSettings = undefined;
		try {
			const setResp = await fetch('./settings.json');
			if (setResp.ok) {
				loadedSettings = await setResp.json();
				this._savedSettings = loadedSettings;
				// Immediately push settings.json contents into localStorage as the source of truth
				localStorage.setItem('settings', JSON.stringify(this._savedSettings));
				// console.log('Loaded settings from settings.json and stored to localStorage:', loadedSettings);
			} else {
				throw new Error('settings.json not found');
			}
		} catch {
			// fallback to localStorage as before
			const savedSettingsStr = localStorage.getItem('settings');
			if (savedSettingsStr) {
				try {
					this._savedSettings = JSON.parse(savedSettingsStr);
					console.log('Loaded settings from localStorage:', this._savedSettings);
				} catch {
					this._savedSettings = {};
				}
			} else {
				this._savedSettings = {};
			}
		}

		const idx = this.currentModelIndex;
		let saved: any | undefined;
		if (this._savedSettings) {
			if (idx === 0 && this._savedSettings['idle']) saved = this._savedSettings['idle'];
			else if (this._savedSettings[idx]) saved = this._savedSettings[idx];
		}
		if (saved) {
			this.lerpParams = {
				bloom: {
					threshold: saved.bloom.threshold,
					strength: saved.bloom.strength,
					radius: saved.bloom.radius,
					dirX: { x: saved.bloom.dirX.x, y: saved.bloom.dirX.y },
					dirY: { x: saved.bloom.dirY.x, y: saved.bloom.dirY.y }
				},
				camera: {
					position: saved.camera.position.slice(),
					rotation: saved.camera.rotation.slice(),
					near: saved.camera.near,
					far: saved.camera.far,
					target: saved.camera.target ? saved.camera.target.slice() : [0, 0, 0]
				},
				light: {
					position: saved.light.position.slice()
				}
			};
			if (this.bloomPass && this.camera && this.dirLight && this.controls) {
				const b = this.bloomPass,
					c = this.camera,
					l = this.dirLight;
				this.lerpStart = {
					bloom: {
						threshold: b.threshold,
						strength: b.strength,
						radius: b.radius,
						dirX: { x: b.dirX.x, y: b.dirX.y },
						dirY: { x: b.dirY.x, y: b.dirY.y }
					},
					camera: {
						position: c.position.toArray(),
						rotation: c.rotation.toArray(),
						near: c.near,
						far: c.far,
						target: this.controls.target ? this.controls.target.toArray() : [0, 0, 0]
					},
					light: {
						position: l.position.toArray()
					}
				};
			} else {
				this.lerpStart = undefined;
			}
			this.lerpProgress = 0;
		}
	}

	initThree(): void {
		this.scene = new THREE.Scene();

		const width = this.parent?.clientWidth || window.innerWidth;
		const height = this.parent?.clientHeight || window.innerHeight;
		this.camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
		this.camera.position.set(0.5, 0.5, 0);

		this.renderer = new THREE.WebGLRenderer({ antialias: true });
		this.renderer.setSize(window.innerWidth, window.innerHeight);
		this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
		this.renderer.toneMappingExposure = 3.21;
		document.getElementById('profiles')?.prepend(this.renderer.domElement);

		this.controls = new OrbitControls(this.camera, this.renderer.domElement);

		const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
		this.scene.add(ambientLight);

		this.dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
		this.dirLight.position.set(5, 10, 7.5);
		this.scene.add(this.dirLight);

		this.composer = new EffectComposer(this.renderer);
		const renderPass = new RenderPass(this.scene, this.camera);
		this.composer.addPass(renderPass);

		const bloomParams: MotionBloomParams = {
			resolution: new Vector2(window.innerWidth, window.innerHeight),
			strength: 0.95,
			radius: 0.5,
			threshold: 0.0,
			dirX: new Vector2(2, 1.1),
			dirY: new Vector2(2, 1.1)
		};
		this.bloomPass = new MotionBloomPass(bloomParams);
		this.composer.addPass(this.bloomPass);

		const outputPass = new OutputPass();
		this.composer.addPass(outputPass);

		this.clock = new THREE.Clock();
	}

	startLoop(): void {
		const animate = () => {
			requestAnimationFrame(animate);
			this.controls.update();

			if (this.originalModel) {
				const t = this.clock.getElapsedTime();
				this.originalModel.position.y = Math.sin(t * 0.1) * 0.01;
				this.originalModel.rotation.y += 0.0002;
				this.originalModel.rotation.z += 0.00009 * Math.sin(t * 0.1);
			}

			// LERP to saved settings if active
			let lerpInProgress = false;
			if (this.lerpParams && this.lerpStart && typeof this.lerpProgress === 'number' && this.lerpProgress < 1) {
				lerpInProgress = true;
				this.lerpProgress = Math.min(1, this.lerpProgress + 0.004);
				const t = this.lerpProgress;
				const eased = t * t * (3 - 2 * t); // smoothstep

				const b = this.bloomPass;
				const lp = this.lerpParams;
				const ls = this.lerpStart;

				// bloom
				b.threshold = THREE.MathUtils.lerp(ls.bloom.threshold, lp.bloom.threshold, eased);
				b.strength = THREE.MathUtils.lerp(ls.bloom.strength, lp.bloom.strength, eased);
				b.radius = THREE.MathUtils.lerp(ls.bloom.radius, lp.bloom.radius, eased);
				b.dirX.set(
					THREE.MathUtils.lerp(ls.bloom.dirX.x, lp.bloom.dirX.x, eased),
					THREE.MathUtils.lerp(ls.bloom.dirX.y, lp.bloom.dirX.y, eased)
				);
				b.dirY.set(
					THREE.MathUtils.lerp(ls.bloom.dirY.x, lp.bloom.dirY.x, eased),
					THREE.MathUtils.lerp(ls.bloom.dirY.y, lp.bloom.dirY.y, eased)
				);

				// camera
				const sPos = new THREE.Vector3().fromArray(ls.camera.position);
				const tPos = new THREE.Vector3().fromArray(lp.camera.position);
				this.camera.position.copy(sPos.lerp(tPos, eased));

				const sRot = new THREE.Euler().fromArray(ls.camera.rotation);
				const tRot = new THREE.Euler().fromArray(lp.camera.rotation);
				const qS = new THREE.Quaternion().setFromEuler(sRot);
				const qT = new THREE.Quaternion().setFromEuler(tRot);
				this.camera.setRotationFromQuaternion(qS.slerp(qT, eased));

				this.camera.near = THREE.MathUtils.lerp(ls.camera.near, lp.camera.near, eased);
				this.camera.far = THREE.MathUtils.lerp(ls.camera.far, lp.camera.far, eased);
				this.camera.updateProjectionMatrix();

				if (ls.camera.target && lp.camera.target) {
					const sT = new THREE.Vector3().fromArray(ls.camera.target);
					const tT = new THREE.Vector3().fromArray(lp.camera.target);
					this.controls.target.copy(sT.lerp(tT, eased));
					if (!/Mobi|Android/i.test(navigator.userAgent)) {
						const shift = 0.1;
						const right = new THREE.Vector3();
						this.camera.getWorldDirection(right);
						right.cross(this.camera.up).normalize();
						this.camera.position.addScaledVector(right, -shift);
						this.controls.target.addScaledVector(right, -shift);
					}
				}

				// light
				const sL = new THREE.Vector3().fromArray(ls.light.position);
				const tL = new THREE.Vector3().fromArray(lp.light.position);
				this.dirLight.position.copy(sL.lerp(tL, eased));

				if (t >= 1) {
					this.lerpParams = undefined;
					this.lerpStart = undefined;
					this.lerpProgress = undefined;
					this.baseBloomRadius = this.bloomPass.radius;
				}
			}

			// subtle bloom radius pulse when idle
			if (!lerpInProgress) {
				if (typeof this.baseBloomRadius !== 'number') this.baseBloomRadius = this.bloomPass.radius;
				const target = this.baseBloomRadius + Math.sin(this.clock.getElapsedTime() * 1.0) * 0.2;
				this.bloomPass.radius = THREE.MathUtils.lerp(this.bloomPass.radius, target, 0.05);
			}

			this.composer.render();
		};
		animate();
	}

	bindEvents(): void {
		window.addEventListener('resize', () => {
			const width = this.parent?.clientWidth || window.innerWidth;
			const height = this.parent?.clientHeight || window.innerHeight;
			this.camera.aspect = width / height;
			this.camera.updateProjectionMatrix();
			this.renderer.setSize(width, height);
			this.composer.setSize(width, height);
		});

		window.addEventListener('keydown', (event) => {
			if (event.key === 'p' || event.key === 'P') {
				if (!this.allowPane) return;
				const paneElem = (this._pane as any).element as HTMLElement;
				if (!paneElem) return;
				paneElem.style.display = paneElem.style.display === 'none' ? '' : 'none';
			} else if (event.key === 's' || event.key === 'S') {
				// export all settings (download)
				if (!this.allowPane) return;
				this.saveAllSettings();
			} else if (event.key === 'e' || event.key === 'E') {
				// save current model settings to localStorage
				if (!this.allowPane) return;
				this.persistSettingsToLocal();
			} else if (event.key === '0') {
				if (!this.allowPane) return;
				this.loadModel('0'); // optional: idle slot
			}
		});
	}

	loadModel(index: string, direction: 'left' | 'right' = 'left'): void {
		this.currentModelIndex = parseInt(index, 10) || 0;
		this.activeLoadId++;
		const loadId = this.activeLoadId;

		if (this.originalModel) {
			this.animateModelExit(this.originalModel, direction);
			this.originalModel = null;
		}
		if (this.axisHelper) {
			this.scene.remove(this.axisHelper);
			this.axisHelper = null;
		}
		this.originalMaterials = [];
		this.depthMeshes = [];

		this.multipleModels.forEach((g) => {
			this.disposeScene(g);
			this.scene.remove(g);
		});
		this.multipleModels = [];

		const cacheKey = index;
		const cached = this.modelCache.get(cacheKey);

		if (cached) {
			// clone cached (including materials)
			const clone = cached.clone(true);
			clone.traverse((child: THREE.Object3D) => {
				const mesh = child as THREE.Mesh;
				if ((mesh as any).isMesh) {
					const mat = mesh.material as THREE.Material | THREE.Material[];
					if (Array.isArray(mat)) mesh.material = mat.map((m) => m.clone());
					else mesh.material = mat.clone();
				}
			});
			this.handleModel(clone, index, loadId);
		} else {
			const loader = new GLTFLoader();
			const draco = new DRACOLoader();
			draco.setDecoderPath('/draco/');
			draco.setDecoderConfig({ type: 'js' });
			loader.setDRACOLoader(draco);

			loader.load(
				`./objects/${index}.glb`,
				(gltf) => {
					this.onLoadModel(gltf, index, loadId, cacheKey);
				},
				(_progress) => {
					// console.log(_progress);
				},
				(error) => {
					console.error(error);
				}
			);
			return;
		}

		// also prune cache when using cached model
		const min = this.currentModelIndex - 5;
		const max = this.currentModelIndex + 5;
		for (const [k, scene] of this.modelCache.entries()) {
			const n = Number(k);
			if (n < min || n > max) {
				this.disposeScene(scene);
				this.modelCache.delete(k);
			}
		}
	}

	handleModel(root: THREE.Group, index: string, loadId: number): void {
		if (loadId !== this.activeLoadId) {
			this.disposeScene(root);
			return;
		}
		root.traverse((child) => {
			const mesh = child as THREE.Mesh;
			if ((mesh as any).isMesh) {
				const mat = mesh.material as THREE.Material | THREE.Material[];
				if (Array.isArray(mat)) this.originalMaterials.push(...mat);
				else this.originalMaterials.push(mat);
				this.depthMeshes.push(mesh);
			}
		});

		this.scene.add(root);
		this.originalModel = root;

		// center model at origin
		const center = new THREE.Vector3();
		new THREE.Box3().setFromObject(root).getCenter(center);
		root.position.sub(center);

		this.applySettingsFor(index);

		// LERP to saved settings (from localStorage/settings.json), including idle "0" -> "idle"
		const saved = this._savedSettings
			? (index === '0' && this._savedSettings['idle']) || this._savedSettings[index]
			: undefined;

		if (saved) {
			// target
			this.lerpParams = {
				bloom: {
					threshold: saved.bloom.threshold,
					strength: saved.bloom.strength,
					radius: saved.bloom.radius,
					dirX: { x: saved.bloom.dirX.x, y: saved.bloom.dirX.y },
					dirY: { x: saved.bloom.dirY.x, y: saved.bloom.dirY.y }
				},
				camera: {
					position: saved.camera.position.slice(),
					rotation: saved.camera.rotation.slice(),
					near: saved.camera.near,
					far: saved.camera.far,
					target: saved.camera.target ? saved.camera.target.slice() : [0, 0, 0]
				},
				light: {
					position: saved.light.position.slice()
				}
			};

			// start (current)
			const b = this.bloomPass;
			const c = this.camera;
			const l = this.dirLight;
			this.lerpStart = {
				bloom: {
					threshold: b.threshold,
					strength: b.strength,
					radius: b.radius,
					dirX: { x: b.dirX.x, y: b.dirX.y },
					dirY: { x: b.dirY.x, y: b.dirY.y }
				},
				camera: {
					position: c.position.toArray(),
					rotation: c.rotation.toArray(),
					near: c.near,
					far: c.far,
					target: this.controls.target ? this.controls.target.toArray() : [0, 0, 0]
				},
				light: {
					position: l.position.toArray()
				}
			};
			this.lerpProgress = 0;

			// sync pane fields if present
			if (this.bloomParams) {
				this.bloomParams.threshold = saved.bloom.threshold;
				this.bloomParams.strength = saved.bloom.strength;
				this.bloomParams.radius = saved.bloom.radius;
				this.bloomParams.dirX.x = saved.bloom.dirX.x;
				this.bloomParams.dirX.y = saved.bloom.dirX.y;
				this.bloomParams.dirY.x = saved.bloom.dirY.x;
				this.bloomParams.dirY.y = saved.bloom.dirY.y;
			}
			if ((this._pane as any)?.refresh) (this._pane as any).refresh();
		}
	}

	onLoadModel(gltf: GLTF, index: string, loadId: number, cacheKey: string): void {
		if (loadId !== this.activeLoadId) {
			this.disposeScene(gltf.scene);
			return;
		}

		// cache a deep clone (with cloned materials)
		const cachedScene = gltf.scene.clone(true);
		cachedScene.traverse((child: THREE.Object3D) => {
			const mesh = child as THREE.Mesh;
			if ((mesh as any).isMesh) {
				const mat = mesh.material as THREE.Material | THREE.Material[];
				if (Array.isArray(mat)) mesh.material = mat.map((m) => m.clone());
				else mesh.material = mat.clone();
			}
		});
		this.modelCache.set(cacheKey, cachedScene);

		this.handleModel(gltf.scene, index, loadId);

		// sliding window cache ±5
		const min = this.currentModelIndex - 5;
		const max = this.currentModelIndex + 5;
		for (const [k, scene] of this.modelCache.entries()) {
			const n = Number(k);
			if (n < min || n > max) {
				this.disposeScene(scene);
				this.modelCache.delete(k);
			}
		}
	}

	applySettingsFor(index: string): void {
		const s = this.settings?.[index];
		if (!s) return;

		// console.log(s);

		// Bloom
		this.bloomPass.threshold = s.bloom.threshold;
		this.bloomPass.strength = s.bloom.strength;
		this.bloomPass.radius = s.bloom.radius;
		this.bloomPass.dirX.set(s.bloom.dirX.x, s.bloom.dirX.y);
		this.bloomPass.dirY.set(s.bloom.dirY.x, s.bloom.dirY.y);

		// Camera
		const [px, py, pz] = s.camera.position;
		this.camera.position.set(px - 0, py, pz);
		const r = s.camera.rotation;
		if (r.length === 4) {
			const [rx, ry, rz, order] = r as [number, number, number, EulerOrder];
			this.camera.setRotationFromEuler(new THREE.Euler(rx, ry, rz, order));
		} else {
			const [rx, ry, rz] = r as [number, number, number];
			this.camera.setRotationFromEuler(new THREE.Euler(rx, ry, rz));
		}
		this.camera.near = s.camera.near;
		this.camera.far = s.camera.far;
		this.camera.updateProjectionMatrix();

		// Controls target
		if (Array.isArray(s.camera.target)) {
			const [tx, ty, tz] = s.camera.target;
			this.controls.target.set(tx - 0, ty, tz);
			// this.controls.target.fromArray(s.camera.target);
		} else {
			// optional default if target absent
			this.controls.target.set(0, 0, 0);
		}
		this.controls.update();

		// // Desktop-only camera shift to the left
		// if (!/Mobi|Android/i.test(navigator.userAgent)) {
		// 	// this.camera.position.x -= 1;
		// 	this.camera.updateProjectionMatrix();
		// }

		// Light
		this.dirLight.position.fromArray(s.light.position);
	}

	disposeScene(root: THREE.Object3D | null | undefined): void {
		if (!root) return;
		root.traverse((child) => {
			const mesh = child as THREE.Mesh;
			if ((mesh as any).isMesh) {
				if (mesh.geometry) mesh.geometry.dispose();
				const mat = mesh.material as THREE.Material | THREE.Material[];
				if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
				else mat?.dispose();
			}
		});
	}

	animateModelExit(model: THREE.Group, direction: 'left' | 'right'): void {
		const startX = model.position.x;
		const targetX = startX + (direction === 'left' ? -0.2 : 0.2);
		const startScale = model.scale.clone();
		const targetScale = new THREE.Vector3(0, 0, 0);

		const bloomStart = this.bloomPass.strength;
		const bloomPeak = bloomStart + 1.0;

		let p = 0;
		const step = () => {
			if (p < 1) {
				p = Math.min(1, p + 0.03);
				model.position.x = THREE.MathUtils.lerp(startX, targetX, p);
				model.scale.set(
					THREE.MathUtils.lerp(startScale.x, targetScale.x, p),
					THREE.MathUtils.lerp(startScale.y, targetScale.y, p),
					THREE.MathUtils.lerp(startScale.z, targetScale.z, p)
				);
				if (p < 0.5) {
					this.bloomPass.strength = THREE.MathUtils.lerp(bloomStart, bloomPeak, p * 2);
				} else {
					this.bloomPass.strength = THREE.MathUtils.lerp(bloomPeak, bloomStart, (p - 0.5) * 2);
				}
				requestAnimationFrame(step);
			} else {
				this.bloomPass.strength = bloomStart;
				this.scene.remove(model);
				this.disposeScene(model);
			}
		};
		step();
	}

	persistSettingsToLocal(): void {
		if (!this._savedSettings) {
			const s = localStorage.getItem('settings');
			try {
				this._savedSettings = s ? JSON.parse(s) : {};
			} catch {
				this._savedSettings = {};
			}
		}
		const id = this.currentModelIndex === 0 ? 'idle' : String(this.currentModelIndex);
		if (!this._savedSettings[id]) this._savedSettings[id] = {};

		const payload = {
			bloom: {
				threshold: this.bloomPass.threshold,
				strength: this.bloomPass.strength,
				radius: this.bloomPass.radius,
				dirX: { x: this.bloomPass.dirX.x, y: this.bloomPass.dirX.y },
				dirY: { x: this.bloomPass.dirY.x, y: this.bloomPass.dirY.y }
			},
			camera: {
				position: this.camera.position.toArray(),
				rotation: this.camera.rotation.toArray(),
				near: this.camera.near,
				far: this.camera.far,
				target: this.controls?.target ? this.controls.target.toArray() : [0, 0, 0]
			},
			light: { position: this.dirLight.position.toArray() }
		};

		this._savedSettings[id] = payload;
		localStorage.setItem('settings', JSON.stringify(this._savedSettings));

		console.log(payload);
	}

	saveAllSettings(): void {
		if (this.currentModelIndex === 0) console.log('Saving idle mode settings...');
		this.persistSettingsToLocal();

		const json = JSON.stringify(this._savedSettings, null, 2);
		const blob = new Blob([json], { type: 'application/json' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = 'settings.json';
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	}

	initPane(): void {
		this._pane = new Pane();
		this._pane.registerPlugin(TweakpaneRotation as any);

		// Renderer
		this.rendererParams = {
			toneMappingExposure: this.renderer.toneMappingExposure,
			toneMapping: this.renderer.toneMapping
		};
		const rendererFolder = (this._pane as any).addFolder({ title: 'Renderer' }) as any;
		rendererFolder
			.addBinding(this.rendererParams, 'toneMappingExposure', { min: 0, max: 10, step: 0.01 })
			.on('change', (ev: any) => {
				this.renderer.toneMappingExposure = ev.value;
			});
		rendererFolder
			.addBinding(this.rendererParams, 'toneMapping', {
				options: {
					NoToneMapping: THREE.NoToneMapping,
					LinearToneMapping: THREE.LinearToneMapping,
					ReinhardToneMapping: THREE.ReinhardToneMapping,
					CineonToneMapping: THREE.CineonToneMapping,
					ACESFilmicToneMapping: THREE.ACESFilmicToneMapping
				}
			})
			.on('change', (ev: any) => {
				this.renderer.toneMapping = ev.value;
				this.scene.traverse((child) => {
					// @ts-ignore
					if (child.isMesh && (child as any).material) (child as any).material.needsUpdate = true;
				});
			});

		// MotionBloom
		this.bloomParams = {
			threshold: this.bloomPass.threshold,
			strength: this.bloomPass.strength,
			radius: this.bloomPass.radius,
			dirX: { x: this.bloomPass.dirX.x, y: this.bloomPass.dirX.y },
			dirY: { x: this.bloomPass.dirY.x, y: this.bloomPass.dirY.y }
		};
		const bloomFolder = (this._pane as any).addFolder({ title: 'MotionBloom' }) as any;
		bloomFolder
			.addBinding(this.bloomParams, 'threshold', { min: 0, max: 1, step: 0.01 })
			.on('change', (ev: any) => {
				this.bloomPass.threshold = ev.value;
			});
		bloomFolder.addBinding(this.bloomParams, 'strength', { min: 0, max: 3, step: 0.01 }).on('change', (ev: any) => {
			this.bloomPass.strength = ev.value;
		});
		bloomFolder.addBinding(this.bloomParams, 'radius', { min: 0, max: 1, step: 0.01 }).on('change', (ev: any) => {
			this.bloomPass.radius = ev.value;
		});
		bloomFolder
			.addBinding(this.bloomParams.dirX, 'x', { min: -5, max: 5, step: 0.01, label: 'dirX.x' })
			.on('change', (ev: any) => {
				this.bloomPass.dirX.x = ev.value;
			});
		bloomFolder
			.addBinding(this.bloomParams.dirX, 'y', { min: -5, max: 5, step: 0.01, label: 'dirX.y' })
			.on('change', (ev: any) => {
				this.bloomPass.dirX.y = ev.value;
			});
		bloomFolder
			.addBinding(this.bloomParams.dirY, 'x', { min: -5, max: 5, step: 0.01, label: 'dirY.x' })
			.on('change', (ev: any) => {
				this.bloomPass.dirY.x = ev.value;
			});
		bloomFolder
			.addBinding(this.bloomParams.dirY, 'y', { min: -5, max: 5, step: 0.01, label: 'dirY.y' })
			.on('change', (ev: any) => {
				this.bloomPass.dirY.y = ev.value;
			});

		// Lights (rotation-from-vector)
		const dirPosNormalized = this.dirLight.position.clone().normalize();
		this.lightParams = {
			rotation: new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), dirPosNormalized)
		};
		const lightsFolder = (this._pane as any).addFolder({ title: 'Lights' }) as any;
		lightsFolder.addBinding(this.lightParams, 'rotation', { view: 'rotation' }).on('change', (ev: any) => {
			const q = ev.value as THREE.Quaternion;
			const dir = new THREE.Vector3(0, 0, 1).applyQuaternion(q).normalize();
			const dist = this.dirLight.position.length();
			this.dirLight.position.copy(dir.multiplyScalar(dist));
		});

		const exportFolder = (this._pane as any).addFolder({ title: 'Export / Settings' }) as any;
		exportFolder.addButton({ title: 'Save Settings' }).on('click', () => this.saveAllSettings());
	}
}
